import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { DecoratorNode, LexicalEditor, NodeKey, SerializedLexicalNode, Spread, $getNodeByKey } from "lexical";

// Type for serialized ImageNode
type SerializedImageNode = Spread<
  {
    src: string;
    altText: string;
    width?: number;
    height?: number;
  },
  SerializedLexicalNode
>;

// ImageNode class
export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __width: number;
  __height: number;

  // Get the type of node
  static getType() {
    return "image";
  }

  // Clone the node
  static clone(node: ImageNode) {
    return new ImageNode(node.__src, node.__altText, node.__width, node.__height, node.__key);
  }

  // Constructor
  constructor(src: string, altText: string, width: number = 300, height: number = 200, key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width;
    this.__height = height;
  }

  // Create DOM element
  createDOM(): HTMLElement {
    return document.createElement("div");
  }

  // Update DOM
  updateDOM(): boolean {
    return false;
  }

  // Import serialized node
  static importJSON(serializedNode: SerializedImageNode) {
    const { src, altText, width, height } = serializedNode;
    return new ImageNode(src, altText, width, height);
  }

  // Export serialized node
  exportJSON(): SerializedImageNode {
    return {
      src: this.__src,
      altText: this.__altText,
      width: this.__width,
      height: this.__height,
      type: ImageNode.getType(),
      version: 1,
    };
  }

  // Export DOM representation
  exportDOM() {
    const img = document.createElement("img");
    img.src = this.__src;
    img.alt = this.__altText;
    img.width = this.__width;
    img.height = this.__height;
    return { element: img };
  }

  // Decorate component for rendering in the editor
  decorate(editor: LexicalEditor, config: any) {
    return (
      <ImageComponent
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        height={this.__height}
        nodeKey={this.getKey()}
        editor={editor}
      />
    );
  }
}

// Create ImageNode instance
export const $createImageNode = (src: string, altText: string, width?: number, height?: number) => {
  return new ImageNode(src, altText, width, height);
};

// ImageComponent Props type
type ImageComponentProps = {
  src: string;
  altText: string;
  width: number;
  height: number;
  nodeKey: string;
  editor: LexicalEditor;
};

// ImageComponent to handle rendering and resizing
const ImageComponent = ({ src, altText, width, height, nodeKey, editor }: ImageComponentProps) => {
  const [currentWidth, setCurrentWidth] = useState(width);
  const [currentHeight, setCurrentHeight] = useState(height);
  const [isSelected, setIsSelected] = useState(false);
  const aspectRatio = width / height;

  const imgRef = useRef<HTMLDivElement>(null);

  // Handle image resize
  const handleResize = useCallback(
    (e: React.MouseEvent, direction: string) => {
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = currentWidth;
      const startHeight = currentHeight;

      const onMouseMove = (moveEvent: MouseEvent) => {
        let newWidth = startWidth;
        let newHeight = startHeight;

        if (["horizontal", "diagonal"].includes(direction)) {
          newWidth = startWidth + moveEvent.clientX - startX;
          newHeight = newWidth / aspectRatio;
        }

        if (["vertical", "diagonal"].includes(direction)) {
          newHeight = startHeight + moveEvent.clientY - startY;
          newWidth = newHeight * aspectRatio;
        }

        setCurrentWidth(newWidth);
        setCurrentHeight(newHeight);
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);

        const imageNode = $getNodeByKey(nodeKey) as ImageNode;
        if (imageNode) {
          imageNode.__width = currentWidth;
          imageNode.__height = currentHeight;
        }
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [currentWidth, currentHeight, aspectRatio, nodeKey]
  );

  const handleClick = () => {
    setIsSelected(!isSelected);
  };

  // Handle delete key press
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isSelected && (e.key === "Delete" || e.key === "Backspace")) {
        editor.update(() => {
          const imageNode = $getNodeByKey(nodeKey);
          if (imageNode) {
            imageNode.remove();
          }
        });
      }
    },
    [isSelected, editor, nodeKey]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const resizeHandleStyles = useMemo(
    () => ({
      position: "absolute" as "absolute",
      width: 10,
      height: 10,
      backgroundColor: "blue",
      cursor: "nwse-resize",
    }),
    []
  );

  return (
    <div
      ref={imgRef}
      onClick={handleClick}
      style={{
        position: "relative",
        width: currentWidth,
        height: currentHeight,
        display: "inline-block",
        border: isSelected ? "2px solid blue" : "none",
      }}
    >
      <img
        src={src}
        alt={altText}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />

      {isSelected && (
        <>
          <div
            onMouseDown={(e) => handleResize(e, "diagonal")}
            style={{ ...resizeHandleStyles, top: -5, left: -5 }}
          ></div>
          <div
            onMouseDown={(e) => handleResize(e, "diagonal")}
            style={{ ...resizeHandleStyles, top: -5, right: -5 }}
          ></div>
          <div
            onMouseDown={(e) => handleResize(e, "diagonal")}
            style={{ ...resizeHandleStyles, bottom: -5, left: -5 }}
          ></div>
          <div
            onMouseDown={(e) => handleResize(e, "diagonal")}
            style={{ ...resizeHandleStyles, bottom: -5, right: -5 }}
          ></div>
        </>
      )}
    </div>
  );
};
